import { useCallback, useEffect, useRef } from "react";
import { DRAWIO_ORIGIN } from "../constants";
import {
  DrawIoHandlers,
  TDrawIoActions,
  TDrawIoEvents,
  UseDrawIoMessagingReturn,
} from "../types";

export const useDrawIoMessaging = (
  iframeRef: React.RefObject<HTMLIFrameElement>,
  handlers: DrawIoHandlers,
): UseDrawIoMessagingReturn => {
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== DRAWIO_ORIGIN) return;

      let data: TDrawIoEvents;

      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      switch (data.event) {
        case "configure":
          handlersRef.current.onConfigure?.();
          break;
        case "init":
          handlersRef.current.onInit();
          break;
        case "exit":
          handlersRef.current.onExit?.();
          break;
        case "export":
          handlersRef.current.onExport?.(
            data.data,
            data.format,
            data.message.message,
          );
          break;
        case "autosave":
          handlersRef.current.onAutosave?.(data?.xml);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const sendAction = useCallback(
    (action: TDrawIoActions) => {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify(action),
        DRAWIO_ORIGIN,
      );
    },
    [iframeRef],
  );

  return { sendAction };
};
